import React, { useState } from 'react';
import { Check, AlertTriangle, Scan } from 'lucide-react';
import { refillHandshake } from '../../lib/api';

const seed = {
  pouch_id: '',
  bottle_id: 'SH-042-0188',
  pouch_capacity_ml: 500,
  volume_before_ml: 120,
  volume_after_ml: 600,
};

const RefillsLog = ({ partnerId, initialRefills = [], onRefilled }) => {
  const [refills, setRefills] = useState(initialRefills);
  const [form, setForm] = useState(seed);
  const [state, setState] = useState({ loading: false, err: '' });

  const update = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: ['pouch_capacity_ml', 'volume_before_ml', 'volume_after_ml'].includes(k)
        ? Number(e.target.value)
        : e.target.value,
    }));

  const onHandshake = async (e) => {
    e.preventDefault();
    setState({ loading: true, err: '' });
    try {
      const res = await refillHandshake({ partner_id: partnerId, ...form });
      setRefills((prev) => [res, ...prev]);
      setForm({ ...seed, pouch_id: '' });
      setState({ loading: false, err: '' });
      if (onRefilled) onRefilled(res);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Handshake failed.';
      setState({
        loading: false,
        err: typeof detail === 'string' ? detail : 'Please check sensor values.',
      });
    }
  };

  const delta = form.volume_after_ml - form.volume_before_ml;
  const capacity = form.pouch_capacity_ml;
  const withinTolerance =
    delta > 0 && delta >= capacity * 0.85 && delta <= capacity * 1.15;

  return (
    <section data-testid="partner-refills" className="grid grid-cols-1 lg:grid-cols-12 gap-10">
      <form
        onSubmit={onHandshake}
        data-testid="refill-handshake-form"
        className="lg:col-span-5 p-8 border border-[#C1D7E2] bg-[#F4F8F9]"
      >
        <div className="flex items-center gap-3 mb-5">
          <Scan size={18} strokeWidth={1.3} className="text-[#7C9CB1]" />
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF]">
            Refill Handshake · NTAG 22x DNA
          </p>
        </div>
        <h3 className="font-serif text-2xl text-[#7C9CB1] font-light leading-tight mb-6">
          Scan the pouch + confirm the sensor.
        </h3>

        <div className="space-y-4">
          <Field label="Pouch ID (QR)">
            <input
              required
              value={form.pouch_id}
              onChange={update('pouch_id')}
              placeholder="PCH-0042"
              data-testid="refill-pouch-id"
              className="refill-input"
            />
          </Field>
          <Field label="Bottle ID">
            <input
              required
              value={form.bottle_id}
              onChange={update('bottle_id')}
              data-testid="refill-bottle-id"
              className="refill-input"
            />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Capacity (ml)">
              <input
                type="number"
                value={form.pouch_capacity_ml}
                onChange={update('pouch_capacity_ml')}
                data-testid="refill-capacity"
                className="refill-input"
              />
            </Field>
            <Field label="Before (ml)">
              <input
                type="number"
                value={form.volume_before_ml}
                onChange={update('volume_before_ml')}
                data-testid="refill-before"
                className="refill-input"
              />
            </Field>
            <Field label="After (ml)">
              <input
                type="number"
                value={form.volume_after_ml}
                onChange={update('volume_after_ml')}
                data-testid="refill-after"
                className="refill-input"
              />
            </Field>
          </div>

          <div
            data-testid="refill-preview"
            className={`font-mono text-[11px] tracking-[0.18em] uppercase px-3 py-2 border ${
              withinTolerance
                ? 'border-[#7C9CB1] text-[#7C9CB1] bg-[#C1D7E2]/40'
                : 'border-[#90B0BF]/60 text-[#90B0BF] bg-[#F4F8F9]'
            }`}
          >
            {withinTolerance ? (
              <span className="inline-flex items-center gap-2">
                <Check size={12} strokeWidth={1.5} /> Sensor delta {delta.toFixed(0)}ml
                matches pouch
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                <AlertTriangle size={12} strokeWidth={1.5} /> Delta {delta.toFixed(0)}ml
                out of tolerance
              </span>
            )}
          </div>
        </div>

        <button
          type="submit"
          disabled={state.loading}
          data-testid="refill-submit"
          className="mt-6 bg-[#7C9CB1] text-[#F4F8F9] px-8 py-3 text-[11px] tracking-[0.22em] uppercase hover:bg-[#90B0BF] transition-colors disabled:opacity-60"
        >
          {state.loading ? 'Verifying…' : 'Verify refill'}
        </button>
        {state.err && (
          <p data-testid="refill-error" className="mt-3 text-sm text-[#7C9CB1]">
            {state.err}
          </p>
        )}
      </form>

      <div className="lg:col-span-7" data-testid="refill-log">
        <h3 className="font-serif text-2xl text-[#7C9CB1] font-light mb-5">
          Refill ledger
        </h3>
        {refills.length === 0 ? (
          <p className="font-serif italic text-[#90B0BF] py-6">
            No refills logged yet.
          </p>
        ) : (
          <ol className="divide-y divide-[#C1D7E2]">
            {refills.map((r, i) => (
              <li
                key={r.id}
                data-testid={`refill-row-${i}`}
                className="py-4 flex items-start justify-between gap-4 flex-wrap"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3">
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${
                        r.verified ? 'bg-[#7C9CB1]' : 'bg-[#90B0BF]/60'
                      }`}
                    />
                    <p className="font-serif text-base text-[#7C9CB1]">
                      Pouch {r.pouch_id} → bottle {r.bottle_id}
                    </p>
                  </div>
                  <p className="mt-1 font-mono text-[11px] text-[#90B0BF]">
                    {new Date(r.created_at).toLocaleString()} · hash {r.chain_hash}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-sm text-[#7C9CB1]">
                    +{r.volume_delta_ml.toFixed(0)} ml
                  </p>
                  <p className="font-mono text-[11px] text-[#90B0BF]">
                    +{r.seva_awarded.toFixed(2)} SEVA
                  </p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      <style>{`
        .refill-input {
          width: 100%;
          background: transparent;
          border: 0;
          border-bottom: 1px solid rgba(124,156,177,0.4);
          color: #7C9CB1;
          font-family: 'Outfit', sans-serif;
          font-weight: 300;
          padding: 8px 2px;
          font-size: 14px;
        }
        .refill-input:focus { outline: none; border-bottom-color: #7C9CB1; }
      `}</style>
    </section>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF]">
      {label}
    </span>
    <div className="mt-1">{children}</div>
  </label>
);

export default RefillsLog;
